/* eslint-disable no-undef */
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, } from '@angular/core';
import { FormControl } from '@angular/forms';
import { QueryParams } from '../../types/query-params';
import { ApiService } from '../../services/api.service';
import { DateTime } from 'luxon';
import { dateTimeDateToExcelDate } from '../../../functions/util';
import { FEEDER_TYPES, FeederMenuType } from '../../types/menu';

@Component({
  selector: 'feeder-query-params',
  template: require('./feeder-query-params.component.html'),
})
export class FeederQueryParamsComponent implements OnInit, OnChanges {
  readonly feederTypes = FEEDER_TYPES;
  replaceNullOptions = [
    { value: '', label: '' },
    { value: 'nv', label: '#NV' },
    { value: 'na', label: 'N/A' },
    { value: 'skip', label: 'SKIP' },
    { value: 'keep', label: 'KEEP' },
    { value: 'drop', label: 'DROP' },
    { value: 'next', label: 'NEXT' },
    { value: 'next_int', label: 'NEXT_INT' },
    { value: 'next_day', label: 'NEXT_DAY' },
    { value: 'previous', label: 'PREVIOUS' },
    { value: 'previous_int', label: 'PREVIOUS_INT' },
    { value: 'previous_day', label: 'PREVIOUS_DAY' },
  ];

  aggregation_changed: boolean = false;
  showVersion = false;
  version_type: string = '';
  period: string = '';
  version_date = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
  };
  version: string | number = '';
  // supportedTimezones: any[] = [...new Set([...(Intl as any).supportedValuesOf('timeZone'), ...['Turkey', 'Etc/UTC']])]
  supportedTimezones = (Intl as any).supportedValuesOf('timeZone');

  units: any[] = [];
  currencies = [
    'EUR',
    'USD',
    'JPY',
    'BGN',
    'CZK',
    'DKK',
    'GBP',
    'HUF',
    'LTL',
    'LVL',
    'PLN',
    'RON',
    'SEK',
    'CHF',
    'NOK',
    'HRK',
    'RUB',
    'TRY',
    'AUD',
    'BRL',
    'CAD',
    'CNY',
    'HKD',
    'IDR',
    'ILS',
    'INR',
    'KRW',
    'MXN',
    'MYR',
    'NZD',
    'PHP',
    'SGD',
    'THB',
    'ZAR',
  ];

  formControlResolution = new FormControl('');
  formControlAggregation = new FormControl('');
  formControlCurrency = new FormControl('');
  formControlUnit = new FormControl('');
  formControlAggregationScope = new FormControl('');
  formControlAggregationMethod = new FormControl('');
  formControlNullDefault = new FormControl('nv');
  formControlHistoricVersion = new FormControl();
  formControlVersionType = new FormControl();
  formControlVersionPeriod = new FormControl();
  formControlVersionDate = new FormControl(this.version_date);
  formControlVersionName = new FormControl();
  formControlVersionPeriodSteps = new FormControl();
  formControlTimezone = new FormControl('');
  formControlStartOfDay = new FormControl();

  @Input() type!: FeederMenuType;
  @Input() queryParams!: QueryParams;
  @Output() queryParamsChange = new EventEmitter<QueryParams>();

  constructor(@Inject(ApiService) private apiService: ApiService) {}

  ngOnInit() {
    this.apiService.getUnits().subscribe((data) => {
      this.units = data.units;
    });
    this.formControlResolution.valueChanges.subscribe((val) => {
      this.queryParams.resolution = val!;
      this.updateAggregation();
      this.emitEvent();
    });
    this.formControlUnit.valueChanges.subscribe((val) => {
      this.queryParams.unit = val!;
      this.emitEvent();
    });
    this.formControlCurrency.valueChanges.subscribe((val) => {
      this.queryParams.currency = val!;
      this.emitEvent();
    });
    this.formControlAggregation.valueChanges.subscribe((val) => {
      this.queryParams.aggregation = val!;
      this.aggregationChanged();
      this.emitEvent();
    });
    this.formControlAggregationScope.valueChanges.subscribe((val) => {
      this.queryParams.aggregation_scope = val!;
      this.emitEvent();
    });
    this.formControlAggregationMethod.valueChanges.subscribe((val) => {
      this.queryParams.aggregation_method = val!;
      this.emitEvent();
    });
    this.formControlNullDefault.valueChanges.subscribe((val) => {
      this.queryParams.nv_default = val!;
      this.emitEvent();
    });
    this.formControlTimezone.valueChanges.subscribe((val) => {
      this.queryParams.timezone = val!;
      this.emitEvent();
    });
    this.formControlStartOfDay.valueChanges.subscribe((val) => {
      this.queryParams.start_of_day = val;
      this.emitEvent();
    });
    this.formControlHistoricVersion.valueChanges.subscribe((val) => {
      this.showVersion = val;
      this.updateVersion();
      this.emitEvent();
    });
    this.formControlVersionType.valueChanges.subscribe((val) => {
      this.version_type = val;
      this.updateVersion();
      this.emitEvent();
    });
    this.formControlVersionPeriod.valueChanges.subscribe((val) => {
      this.period = val;
      this.updateVersion();
    });
    this.formControlVersionDate.valueChanges.subscribe((val) => {
      this.version_date = val!;
      this.updateVersion();
      this.emitEvent();
    });
    this.formControlVersionName.valueChanges.subscribe((val) => {
      this.version = val;
      this.updateVersion();
      this.emitEvent();
    });
    this.formControlVersionPeriodSteps.valueChanges.subscribe((val) => {
      this.version = val;
      this.updateVersion();
      this.emitEvent();
    });
    this.formControlStartOfDay.addValidators((control) => {
      if (isNaN(control.value)) {
        return {
          startOfDayValidation:
            'Start of day has to be a numeric value between 0 and 23',
        };
      }
      if (control.value < 0 || control.value > 23) {
        return {
          startOfDayValidation:
            'Start of day has to be a numeric value between 0 and 23',
        };
      }
      return null;
    });
    if (this.type === FEEDER_TYPES.FUNDAMENTAL_DATA) {
      this.formControlCurrency.disable({ emitEvent: false });
    } else {
      this.formControlCurrency.enable({ emitEvent: false });
    }
    if (this.formControlAggregation.value === '') {
      this.formControlAggregationMethod.disable({ emitEvent: false });
      this.formControlAggregationScope.disable({ emitEvent: false });
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (this.queryParams.latest_price!) {
      this.formControlResolution.disable({ emitEvent: false });
      this.formControlAggregation.disable({ emitEvent: false });
      this.formControlAggregationScope.disable({ emitEvent: false });
      this.formControlAggregationMethod.disable({ emitEvent: false });
      this.formControlNullDefault.disable({ emitEvent: false });
    } else {
      this.formControlResolution.enable({ emitEvent: false });
      this.formControlAggregation.enable({ emitEvent: false });
      if (this.queryParams.aggregation !== '') {
        this.formControlAggregationScope.enable({ emitEvent: false });
        this.formControlAggregationMethod.enable({ emitEvent: false });
      }
      this.formControlNullDefault.enable({ emitEvent: false });
    }
    if (this.queryParams.auto_update!) {
      this.formControlNullDefault.setValue('');
    }
  }

  updateAggregation() {
    if (!this.aggregation_changed) {
      this.queryParams.aggregation = this.queryParams.resolution;
      this.queryParams.aggregation_scope = 'base';
      this.formControlAggregation.setValue(this.queryParams.resolution!);
      this.formControlAggregationScope.setValue('base');
    }
  }

  updateVersion() {
    if (this.showVersion) {
      if (this.version_type === 'date') {
        console.log(this.version_date);
        this.queryParams.max_version = dateTimeDateToExcelDate(
          DateTime.fromObject({
            year: this.version_date.year,
            month: this.version_date.month,
            day: this.version_date.day,
          })
        );
      } else if (this.version_type === 'version_number') {
        this.queryParams.max_version =
          this.version !== '' ? 'v' + this.version : '';
      } else if (this.version_type === 'version_name') {
        this.queryParams.max_version =
          this.version !== '' ? "v'" + this.version : '';
      } else if (this.version_type === 'period') {
        this.queryParams.max_version =
          this.version !== '' ? `${this.period}:${this.version}` : '';
      } else {
        this.queryParams.max_version = '';
      }
    } else {
      this.queryParams.max_version = '';
    }
  }

  aggregationChanged() {
    this.aggregation_changed = true;
    this.queryParams.aggregation_scope = 'base';
    this.formControlAggregationScope.setValue('base');
    if (this.formControlAggregation.value === '') {
      this.formControlAggregationScope.disable({ emitEvent: false });
      this.formControlAggregationMethod.disable({ emitEvent: false });
    } else {
      this.formControlAggregationScope.enable({ emitEvent: false });
      this.formControlAggregationMethod.enable({ emitEvent: false });
    }
  }

  emitEvent() {
    this.queryParamsChange.emit(this.queryParams);
  }

  filterSupportedTimezones() {
    return this.supportedTimezones.filter((timezone) =>
      timezone
        .toUpperCase()
        .includes(this.formControlTimezone.value!.toUpperCase())
    );
  }
}
